import { Box, CircularProgress } from '@mui/material'
import * as MuiIcons from '@mui/icons-material'

import { Menu, useSidebarState, useTranslate } from '..'
import {
  IconButton,
  Icons,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '../../../components'
import { useBranding, useMenu } from '../../../hooks'
import { ProtectedMenuItem } from './ProtectedMenuItem'
import { ProtectedSection } from './ProtectedSection'

// Dynamic menu item component
function DynamicMenuItem({ item }) {
  // First try MUI icons (used by menu item form), then fall back to custom Icons, then Experimental
  const IconComponent = MuiIcons[item.icon] || Icons[item.icon] || Icons.Experimental

  // Handle external links (e.g., AI Chat)
  if (item.metadata?.isExternal) {
    return (
      <ListItemButton
        onClick={() => {
          window.location.href = item.route
        }}
      >
        <Tooltip title={item.label}>
          <ListItemIcon>
            <IconComponent />
          </ListItemIcon>
        </Tooltip>
        <ListItemText primary={item.label} />
      </ListItemButton>
    )
  }

  // Items without permissions don't need ProtectedMenuItem wrapper
  if (!item.requiredPermissions || item.requiredPermissions.length === 0) {
    return (
      <ListItemButton component={Link} to={item.route}>
        <Tooltip title={item.label}>
          <ListItemIcon>
            <IconComponent />
          </ListItemIcon>
        </Tooltip>
        <ListItemText primary={item.label} />
      </ListItemButton>
    )
  }

  // Standard menu item with permission check
  return (
    <ProtectedMenuItem requiredPermissions={item.requiredPermissions}>
      <ListItemButton component={Link} to={item.route}>
        <Tooltip title={item.label}>
          <ListItemIcon>
            <IconComponent />
          </ListItemIcon>
        </Tooltip>
        <ListItemText primary={item.label} />
      </ListItemButton>
    </ProtectedMenuItem>
  )
}

// Menu styles shared between both layouts
const menuStyles = {
  height: '100%',
  maxHeight: '100%',
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  marginTop: 0,
  backgroundColor: 'var(--light-grey)',
  // Widths live here rather than in index.css. react-admin sets its own
  // (240px open, 56px closed) through emotion, which is injected after the
  // stylesheet, so a plain rule there loses no matter how it is written. Setting
  // them on the component wins because it is the same layer.
  //
  // Open is sized to the longest label ("Recurring Gift Requests") so no row
  // wraps to a second line. Closed carries an icon plus a caption underneath,
  // which a 56px rail cannot.
  '&.RaMenu-open': { width: 290, flex: '0 0 290px' },
  '&.RaMenu-closed': { width: 104, flex: '0 0 104px' },
  // Collapsed rail: the full label will not fit beside the icon, so it goes
  // under it, centred and on one line. An icon on its own is not enough to tell
  // rows apart, which is what made the collapsed nav unusable.
  '&.RaMenu-closed .MuiListItemText-root': {
    flex: '1 1 auto',
    margin: 0,
    textAlign: 'center',
  },
  '&.RaMenu-closed .MuiListItemText-root span': {
    fontSize: 10,
    lineHeight: 1.2,
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '&.RaMenu-closed .MuiListItemButton-root': {
    flexDirection: 'column',
    alignItems: 'center',
    padding: '6px 4px',
  },
  '&.RaMenu-closed .MuiListItemIcon-root': {
    minWidth: 0,
    justifyContent: 'center',
  },
  '&.RaMenu-closed .MuiListSubheader-root': { visibility: 'hidden' },
  // The label column was pinned to 160px, so any label wider than that wrapped
  // regardless of how much room the nav had. Let it take the width that is
  // actually there and the longest labels sit on one line.
  '& .MuiListItemText-root': { flex: '1 1 auto', minWidth: 0 },
  '& .MuiListItemButton-root': {
    padding: '6px 16px',
    '& .MuiListItemText-root span': {
      color: 'black !important',
      textDecoration: 'none !important',
    },
    '& .MuiListItemIcon-root': {
      color: 'primary.dark',
    },
  },
  // MUI pins ListSubheader with position: sticky by default. Combined with a
  // transparent background that meant menu rows scrolled *through* the pinned
  // section header and the two sets of text drew on top of each other. The
  // header scrolls with its section now, and is opaque, so nothing can overlap
  // it even where a sticky header survives.
  '& .MuiListSubheader-root': {
    color: 'var(--darker-grey)',
    position: 'static',
    backgroundColor: 'var(--light-grey)',
  },
  '& .MuiListItemButton-root.active': {
    backgroundColor: 'background.default',
  },
}

// Menu content component (shared between both layouts)
function MenuContent({ menuItems, loading }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  return (
    <>
      {menuItems.map((section) => (
        <ProtectedSection key={section.key} title={section.label}>
          {section.children?.map((item) => (
            <DynamicMenuItem key={item.key} item={item} />
          ))}
        </ProtectedSection>
      ))}
    </>
  )
}

// The collapse control for the sidebar.
//
// It was previously react-admin's stock SidebarToggleButton dropped into an
// otherwise empty list row with its padding zeroed: a hamburger hugging the left
// edge, aligned to neither the icon rail below it nor the panel edge beside it,
// which is why it read as an icon that happened to land there. Its only feedback
// was a 180 degree rotation, and a rotated hamburger is indistinguishable from
// an unrotated one.
//
// This is a header row for the panel it controls instead. It keeps a fixed
// height and a rule beneath it, so the menu begins at a clear line rather than
// immediately under a floating icon, and the chevron names the direction the
// panel is about to move: at the panel's right edge pointing left to collapse,
// centred in the icon rail pointing right to expand. In both states it sits at
// the top of the sidebar, which is where the eye already is.
//
// The Developer Style Guide covers the page header only and says nothing about
// the sidebar or its collapse affordance. Flagged as a gap per FLIGHT-777 rather
// than written up here as a standard of our own.
function SidebarToggle() {
  const [open, setOpen] = useSidebarState()
  const translate = useTranslate()
  const label = translate(open ? 'ra.action.close_menu' : 'ra.action.open_menu', {
    _: open ? 'Collapse menu' : 'Expand menu',
  })

  return (
    <ListItem
      disablePadding
      sx={{
        height: 48,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: open ? 'flex-end' : 'center',
        px: open ? 1 : 0,
        mb: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Tooltip title={label} placement="right" enterDelay={500}>
        <IconButton size="small" onClick={() => setOpen(!open)} aria-label={label} data-testid="sidebar-toggle">
          {open ? <MuiIcons.ChevronLeft /> : <MuiIcons.ChevronRight />}
        </IconButton>
      </Tooltip>
    </ListItem>
  )
}

export function Sidebar(props) {
  const branding = useBranding()
  const { menuItems, loading } = useMenu('ADMIN')

  // Check if sidebar logo should be shown (defaults to false)
  const showSidebarLogo = branding?.showSidebarLogo === true
  const logoSrc = branding?.getAssetValue('logo') || '/givewise-logo-wordmark.png'

  if (!showSidebarLogo) {
    return (
      <Menu
        {...props}
        sx={{
          width: '240px',
          ...menuStyles,
        }}
      >
        <List>
          <SidebarToggle />

          <MenuContent menuItems={menuItems} loading={loading} />
        </List>
      </Menu>
    )
  }

  // If sidebar logo should be shown, wrap Menu in a Box with logo
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        backgroundColor: 'var(--light-grey)',
      }}
    >
      {/* Logo - Fixed at top, does NOT scroll */}
      <Box
        sx={{
          height: '64px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px 16px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        }}
      >
        <img
          src={logoSrc}
          alt={branding?.platformName || 'GiveWise'}
          style={{
            maxHeight: '48px',
            maxWidth: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Menu - Scrollable area */}
      <Menu
        {...props}
        sx={{
          flex: 1,
          ...menuStyles,
        }}
      >
        <List>
          <SidebarToggle />

          <MenuContent menuItems={menuItems} loading={loading} />
        </List>
      </Menu>
    </Box>
  )
}
